<template>
  <div :class="['asc-chips-filter', menu ? 'asc-chips-filter--expanded' : '']">
    <v-menu
      v-model="menu"
      content-class="asc-chips-filter--menu"
      offset-y
      :close-on-content-click="false"
    >
      <template #activator="{ on }">
        <v-btn
          :class="[
            'asc-chips-filter--toggle-button',
            anySelections ? 'asc-chips-filter--toggle-button-active' : '',
          ]"
          text
          @click="on.click"
        >
          <v-icon :size="16"> mdi-filter </v-icon>
        </v-btn>
      </template>
      <div
        :class="[
          'asc-chips-filter--menu-content',
          menuExpanded ? getExpandedHeightClass : '',
        ]"
      >
        <div class="asc-chips-filter--menu-content--label">
          <asc-label uppercase value="Filters"></asc-label>
        </div>

        <template v-if="optionFilters.length > 1">
          <v-radio-group
            v-model="radioIndex"
            class="asc-chips-filter--menu-content--radios"
            row
          >
            <v-radio
              v-for="tab in optionFilters"
              :key="tab.name"
              :label="tab.name"
              :value="tab.value"
            ></v-radio>
          </v-radio-group>
        </template>

        <template v-for="tab in optionFilters">
          <template v-if="radioIndex === tab.value">
            <asc-autocomplete-selector
              :key="tab.name"
              v-model="tab.checkedIds"
              :items="tab.items"
              :label="tab.name"
              placeholder="Select Item"
              component-type="multiple"
              no-array
              dense
              drop-down-size-matches
              :disable-select-all="isSelectAllDisabled(tab)"
              :on-expand-menu="handleExpandMenu"
              :on-collapse-menu="handleCollapseMenu"
            ></asc-autocomplete-selector>
          </template>
        </template>
      </div>

      <div class="asc-chips-filter--button-container">
        <asc-accent-button
          button-text="Apply Filters"
          :on-click="handleApplyFilters"
        ></asc-accent-button>
      </div>
    </v-menu>

    <div class="asc-chips-filter--content">
      <template v-if="anySelections">
        <div
          v-for="(selection, index) in selections"
          :key="index"
          class="asc-chips-filter--chip-content"
        >
          <span class="asc-chips-filter--chip-content-text">{{
            selection
          }}</span>
          <v-btn
            class="asc-chips-filter--chip-content-button"
            icon
            @click="handleChipClose(selection)"
          >
            <v-icon size="16">mdi-close</v-icon>
          </v-btn>
        </div>
      </template>
      <template v-if="!anySelections">
        <div class="asc-chips-filter--chip-content">
          <span class="asc-chips-filter--chip-content-text"
            >No Filters Applied</span
          >
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import AscAccentButton from '@/components/atoms/AscAccentButton'
import AscAutocompleteSelector from '@/components/molecules/AscAutocompleteSelector'
import AscLabel from '@/components/atoms/AscLabel'

export default {
  name: 'AscChipsFilter',

  components: {
    AscAccentButton,
    AscAutocompleteSelector,
    AscLabel,
  },

  props: {
    optionFilters: {
      type: Array,
      default: () => [],
    },
    ataChapters: {
      type: Array,
      default: () => [],
    },
    messageCodes: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      anySelections: false,
      selections: [],
      menu: false,
      toggleIndex: 2,
      radioIndex: null,
      menuExpanded: false,
      buttonList: [
        {
          index: 0,
          text: '1w',
          caption: '1 Week',
        },
        {
          index: 1,
          text: '2w',
          caption: '2 Weeks',
        },
        {
          index: 2,
          text: '1m',
          caption: '1 Month',
        },
        {
          index: 3,
          text: '3m',
          caption: '3 Months',
        },
        {
          index: 4,
          text: '6m',
          caption: '6 Months',
        },
        {
          index: 5,
          text: '1y',
          caption: '1 Year',
        },
      ],
    }
  },

  computed: {
    getExpandedHeightClass() {
      return this.optionFilters.length > 1
        ? 'asc-chips-filter--menu-content-expanded-rbtns'
        : 'asc-chips-filter--menu-content-expanded-no-rbtns'
    },
  },

  watch: {
    optionFilters: {
      handler: function (newValue) {
        this.optionFilters = newValue
        this.selections = this.buildSelections()
        this.anySelections = this.selections.length > 0
      },
      immediate: true,
    },
  },

  mounted() {
    this.radioIndex = '0'
    this.selections = this.buildSelections()
    this.anySelections = this.selections.length > 0
  },

  methods: {
    isSelectAllDisabled(tab) {
      return tab?.disableSelectAll || false
    },

    buildSelections() {
      const [optionFilter] = this.optionFilters.filter(
        item => item.value === this.radioIndex,
      )

      if (optionFilter) {
        if (this.radioIndex === optionFilter.value) {
          const ids = optionFilter.checkedIds || []
          const items = optionFilter.items || []
          const displayItems = ids.map(id => {
            const [filteredItem] = items.filter(item => item.value === id)
            return filteredItem.text
          })
          return displayItems
        }
      }

      return []
    },

    handleButtonToggle(index) {
      this.toggleIndex = index
    },

    handleApplyFilters() {
      this.menu = false
      this.handleInput()
    },

    handleChipClose(selection) {
      const [optionFilter] = this.optionFilters.filter(
        item => item.value === this.radioIndex,
      )
      if (optionFilter) {
        if (this.radioIndex === optionFilter.value) {
          const items = optionFilter.items || []
          const [filteredItem] = items.filter(item => item.text === selection)
          const id = filteredItem.value || 0

          optionFilter.checkedIds = optionFilter.checkedIds.filter(
            item => item !== id,
          )
        }
      }
      this.handleInput()
    },

    handleInput() {
      const filters = {
        radioIndex: this.radioIndex,
      }
      this.optionFilters.forEach(optionFilter => {
        filters[optionFilter.propName] = optionFilter.checkedIds
      })
      this.selections = this.buildSelections()
      this.anySelections = this.selections.length > 0
      this.$emit('filterChanged', filters)
    },

    handleExpandMenu() {
      this.menuExpanded = true
    },

    handleCollapseMenu() {
      this.menuExpanded = false
    },
  },
}
</script>

<style lang="scss">
.asc-chips-filter {
  display: flex;

  &--toggle-button {
    border: 1px solid $gray-400;
    background-color: $primary-white !important;
    height: 32px !important;
    width: 32px !important;
    max-width: 32px !important;
    min-width: 32px !important;
    border-radius: 0 !important;
    margin-right: 8px;

    .v-icon {
      color: $primary-black !important;
    }

    &:hover {
      background-color: $accent-200 !important;
    }
  }

  &--content {
    overflow-x: auto;
    display: flex;

    .asc-chips-filter--chip-content {
      margin: 0 8px 0 0;
      height: 32px;
      background-color: $gray-100;
      text-align: center;
      padding: 0 8px;
      display: flex;

      .asc-chips-filter--chip-content-text {
        display: inline-block;
        line-height: 32px;
        font-size: 14px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        max-width: 150px;
      }

      .asc-chips-filter--chip-content-button {
        color: $primary-black;
        height: 28px;
        width: 28px;
        margin: auto 0;
      }
    }
  }

  &--toggle-button-active {
    .v-icon {
      color: $accent-600 !important;
    }
  }
}

.asc-chips-filter--expanded {
  .asc-chips-filter--toggle-button {
    background-color: $accent-100 !important;

    .v-icon {
      color: $accent-600 !important;
    }
  }
}

.asc-chips-filter--menu-content {
  padding: 16px;
  min-width: 325px;
  max-width: 325px;
  background-color: $primary-white;

  .asc-chips-filter--menu-content--label {
    margin-bottom: 8px;
  }

  .asc-chips-filter--menu-content--radios {
    .v-radio {
      label {
        font-size: 14px;
      }
    }
  }
}

.asc-chips-filter--menu-content-expanded-rbtns {
  height: 570px;
}

.asc-chips-filter--menu-content-expanded-no-rbtns {
  height: 500px;
}

.asc-chips-filter--button-container {
  border-top: 1px solid $gray-300;
  background-color: $primary-white;

  .asc-accent-button-container {
    padding: 16px 0;
    margin: 0 16px;

    .asc-accent-button--accent {
      width: 100%;
    }
  }
}
</style>
